import { REFRESH_TOKEN as REFRESH_TOKEN_KEY, TOKEN } from './constants';

// Shared refresh token mechanism - singleton lock prevents multiple simultaneous refresh calls
// Used by both Apollo Client (GraphQL) and Axios (REST API)
let isRefreshing = false;
let pendingRefreshPromise = null;

// Checks if a JWT token is expired by decoding its payload
export const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    let base64 = token.split('.')[1];
    if (!base64) return true;
    base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    const payload = JSON.parse(atob(base64));
    if (!payload?.exp) return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Fetches new tokens using raw fetch to avoid Apollo/Axios interceptor recursion
const fetchNewTokens = async () => {
  // eslint-disable-next-line no-undef
  const accessToken = localStorage?.getItem(TOKEN);
  // eslint-disable-next-line no-undef
  const refreshToken = localStorage?.getItem(REFRESH_TOKEN_KEY);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  let response;
  try {
    // eslint-disable-next-line no-undef
    response = await fetch(process.env.REACT_APP_SERVER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON?.stringify({
        query: `query refreshToken($data: RefreshTokenInput) {
          refreshToken(data: $data) { token refreshToken }
        }`,
        variables: { data: { accessToken, refreshToken } },
      }),
    });
  } finally {
    clearTimeout(timeoutId);
  }
  if (!response?.ok) {
    throw new Error('Token refresh request failed');
  }
  const result = await response?.json();
  const newToken = result?.data?.refreshToken?.token;
  const newRefreshToken = result?.data?.refreshToken?.refreshToken;
  if (!newToken || !newRefreshToken) {
    throw new Error('Token refresh failed');
  }
  // eslint-disable-next-line no-undef
  localStorage.setItem(TOKEN, newToken);
  // eslint-disable-next-line no-undef
  localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
  return newToken;
};

// Singleton refresh - ensures only one refresh call at a time across both GraphQL and REST
// All concurrent callers share the same promise
export const refreshTokens = () => {
  if (isRefreshing) return pendingRefreshPromise;
  isRefreshing = true;
  pendingRefreshPromise = fetchNewTokens()?.finally(() => {
    isRefreshing = false;
    pendingRefreshPromise = null;
  });
  return pendingRefreshPromise;
};

// Gets a valid token - proactively refreshes if expired
export const getValidToken = async () => {
  // eslint-disable-next-line no-undef
  let token = localStorage?.getItem(TOKEN);
  if (token && isTokenExpired(token)) {
    try {
      token = await refreshTokens();
    } catch {
      // Refresh failed, return existing token
    }
  }
  return token;
};

// Returns whether a refresh is currently in progress
export const getIsRefreshing = () => isRefreshing;

// Returns the pending refresh promise (for waiting on in-progress refreshes)
export const getPendingRefreshPromise = () => pendingRefreshPromise;

// Determines if an auth error should trigger token refresh vs immediate logout
export const isRefreshableAuthError = (extensions, errorMessage) => {
  if (
    errorMessage === 'Tenant not found!' ||
    errorMessage === 'User is disabled!'
  )
    return false;
  if (extensions?.exception?.code === 405) return false;
  return extensions?.code === 'UNAUTHENTICATED';
};
